/**
 * The shopper's first search, kept in the browser so it can ride on the
 * events that the Customer.io destination forwards.
 *
 * `first_search_*` reaches the PostHog person by `$set_once`, but for a
 * shopper who searched while anonymous that only happens at `identify`
 * (see pendingPersonProps.ts). `Registration Completed` fires before that
 * write and `Newsletter Subscribed` usually within a second of it, so the
 * destination, which reads the person as it was when the event was ingested,
 * sees no first search on about half of subscribers. Stamping the values on
 * the event itself removes the race; the destination prefers the person's
 * values and falls back to these.
 *
 * Every saved search belongs to the signed-in account that made it, or to
 * `ANONYMOUS_OWNER` when nobody (or a guest) was signed in, and is only
 * handed back to that account. An anonymous search goes to the first account
 * that signs in after it (`adoptFirstSearch`, and on recall), so an account
 * switch that skips sign-out cleanup (the app's session-auth link signs out
 * of Firebase directly) never carries one shopper's search text to another.
 * Keyed on the Firebase account rather than the PostHog distinct id, which
 * AppProviders re-identifies on its own schedule.
 *
 * First write wins per owner, matching `$set_once`. Storage can be missing or
 * throw; a failure only means the event carries no copy.
 */
const STORAGE_KEY = 'dupe_first_search'

interface Stored {
  owner: string
  props: Record<string, unknown>
}

const read = (): Stored | undefined => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    if (
      parsed &&
      typeof parsed.owner === 'string' &&
      parsed.props &&
      typeof parsed.props === 'object'
    ) {
      return parsed
    }
  } catch {
    // Fall through.
  }
  return undefined
}

const write = (stored: Stored): boolean => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
    return true
  } catch {
    // Storage unavailable: events go without a copy.
    return false
  }
}

export const ANONYMOUS_OWNER = 'anonymous'

/** The owner a search made now belongs to. Guests count as anonymous: a
 *  guest upgrade signs in with a fresh uid. */
export const firstSearchOwner = (
  user: { uid: string; isAnonymous: boolean } | null | undefined
): string => (user && !user.isAnonymous ? user.uid : ANONYMOUS_OWNER)

/** Saves `owner`'s first search. Returns true when this call saved it,
 *  i.e. this search is the owner's first in this browser. A search made
 *  signed out before this account signed in counts as theirs (see
 *  `adoptFirstSearch`), so it is adopted and this one is not the first. */
export const rememberFirstSearch = (
  props: Record<string, unknown>,
  owner: string
): boolean => {
  if (Object.keys(props).length === 0) return false
  const stored = read()
  if (stored?.owner === owner) return false
  if (stored?.owner === ANONYMOUS_OWNER && owner !== ANONYMOUS_OWNER) {
    adoptFirstSearch(owner)
    return false
  }
  // Not saved = not known to be first: blocked storage would otherwise
  // call every search the first.
  return write({ owner, props })
}

/** Whether this browser already holds `owner`'s first search, so the next
 *  one would not be saved (see `rememberFirstSearch`). */
export const hasFirstSearch = (owner: string): boolean =>
  read()?.owner === owner

/** Hands an anonymous visitor's search to the account that signed in. */
export const adoptFirstSearch = (userId: string): void => {
  const stored = read()
  if (stored?.owner !== ANONYMOUS_OWNER) return
  write({ owner: userId, props: stored.props })
}

/** The account's first search, or `{}` when it belongs to someone else. */
export const recallFirstSearch = (
  userId: string | null | undefined
): Record<string, unknown> => {
  if (!userId || userId === ANONYMOUS_OWNER) return {}
  adoptFirstSearch(userId)
  const stored = read()
  return stored?.owner === userId ? stored.props : {}
}

export const forgetFirstSearch = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore.
  }
}
